import { env } from "../env";
import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { createUploadLink } from "apollo-upload-client";
import { getSdk } from "generated.sdk";
import { GraphQLClient } from "graphql-request";
import { sign, verify } from "jsonwebtoken";
import jwtDecode from "jwt-decode";

const REFRESH_TOKEN_MUTATION = `
  mutation RefreshToken {
    refreshToken(revokeRefreshToken: true) {
      success
      errors
      token {
        token
      }
    }
  }
`;

const REVOKE_TOKEN_MUTATION = `
  mutation RevokeToken {
    revokeToken {
      success
    }
  }
`;

let refreshTokenRequest = null;

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }
  try {
    const decoded = JSON.parse(jwtDecode(accessToken).payload);
    const currentTime = Date.now() / 1000;
    const exp = new Date(decoded.exp) / 1000;
    return exp > currentTime;
  } catch (err) {
    return false;
  }
};

const getStoredAccessToken = () => {
  try {
    return localStorage.getItem("accessToken");
  } catch (err) {
    return null;
  }
};

const setSession = (accessToken) => {
  try {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
    } else {
      localStorage.removeItem("accessToken");
    }
  } catch (err) {
    // localStorage can be unavailable after jsdom teardown or in privacy-restricted browsers.
  }
};

const redirectToSignIn = () => {
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const signInPath = "/auth/sign-in";
  if (window.location.pathname === signInPath) {
    return;
  }
  window.location.assign(
    `${signInPath}?returnTo=${encodeURIComponent(currentPath)}`
  );
};

const normalizeGraphQLErrors = (graphQLErrors) => {
  if (!graphQLErrors) {
    return [];
  }

  if (Array.isArray(graphQLErrors)) {
    return graphQLErrors;
  }

  if (Array.isArray(graphQLErrors.errors)) {
    return graphQLErrors.errors;
  }

  return [graphQLErrors];
};

const isAuthError = (error) => {
  const graphQLErrors = normalizeGraphQLErrors(
    error?.graphQLErrors || error?.response?.errors
  );
  const networkError = error?.networkError || error;
  const statusCode = networkError?.statusCode || networkError?.status;

  if (statusCode === 401) {
    return true;
  }

  return graphQLErrors.some((graphQLError) => {
    const code = graphQLError?.extensions?.code || graphQLError?.code;
    const message = (graphQLError?.message || "").toLowerCase();
    return (
      code === "invalid_token" ||
      code === "expired_token" ||
      code === "not_authenticated" ||
      message.includes("invalid token") ||
      message.includes("expired token") ||
      message.includes("not authenticated") ||
      message.includes("authentication credentials")
    );
  });
};

const getOperationName = (operation) => operation?.operationName || "unknown";

const reportApolloError = ({ graphQLErrors, networkError, operation }) => {
  const normalizedGraphQLErrors = normalizeGraphQLErrors(graphQLErrors);

  if (isAuthError({ graphQLErrors, networkError })) {
    return;
  }

  const operationName = getOperationName(operation);

  normalizedGraphQLErrors.forEach((graphQLError) => {
    const message = graphQLError?.message || "GraphQL error";
    const error = new Error(`GraphQL error in ${operationName}: ${message}`);
    error.name = "GraphQLError";

    Sentry.withScope((scope) => {
      scope.setTag("graphql.operation", operationName);
      scope.setContext("graphql", {
        operationName,
        path: graphQLError?.path,
        locations: graphQLError?.locations,
        extensions: graphQLError?.extensions,
      });
      Sentry.captureException(error);
    });
  });

  if (networkError) {
    const error =
      networkError instanceof Error
        ? networkError
        : new Error(networkError?.message || "GraphQL network error");

    Sentry.withScope((scope) => {
      scope.setTag("graphql.operation", operationName);
      scope.setContext("graphql", {
        operationName,
        statusCode: networkError?.statusCode || networkError?.status,
      });
      Sentry.captureException(error);
    });
  }
};

const executeAuthMutation = async (query) => {
  const token = getStoredAccessToken();
  const response = await fetch(env.REACT_APP_GRAPHQL_API_URL, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(`Auth request failed with status ${response.status}`);
  }

  const payload = await response.json();
  if (payload.errors?.length) {
    throw new Error(payload.errors[0].message || "Auth request failed");
  }
  return payload.data;
};

const getRefreshTokenRequest = () => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = executeAuthMutation(REFRESH_TOKEN_MUTATION)
      .then((data) => {
        const refreshToken = data?.refreshToken;
        const accessToken = refreshToken?.token?.token;
        if (!refreshToken?.success || !accessToken) {
          throw new Error("Token refresh failed");
        }
        setSession(accessToken);
        return accessToken;
      })
      .catch((err) => {
        setSession(null);
        throw err;
      })
      .finally(() => {
        refreshTokenRequest = null;
      });
  }
  return refreshTokenRequest;
};

const refreshAccessToken = async ({ redirectOnFailure = true } = {}) => {
  try {
    return await getRefreshTokenRequest();
  } catch (err) {
    if (redirectOnFailure) {
      redirectToSignIn();
    }
    throw err;
  }
};

const revokeRefreshToken = async () => {
  try {
    await executeAuthMutation(REVOKE_TOKEN_MUTATION);
  } finally {
    setSession(null);
  }
};

const httpLink = createUploadLink({
  uri: env.REACT_APP_GRAPHQL_API_URL,
  credentials: "include",
});

const authLink = setContext(async (_, { headers }) => {
  let token = getStoredAccessToken();
  if (token && !isValidToken(token)) {
    token = await refreshAccessToken().catch(() => null);
  }

  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  };
});

const authErrorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (
      !isAuthError({ graphQLErrors, networkError }) ||
      operation.getContext().authRetry
    ) {
      return undefined;
    }

    return new Observable((observer) => {
      let subscriber;
      refreshAccessToken()
        .then((token) => {
          operation.setContext(({ headers = {} }) => ({
            authRetry: true,
            headers: {
              ...headers,
              Authorization: `JWT ${token}`,
            },
          }));

          subscriber = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch((err) => {
          observer.error(err);
        });

      return () => {
        subscriber?.unsubscribe();
      };
    });
  }
);

const sentryErrorLink = onError(reportApolloError);

const userClient = new ApolloClient({
  link: ApolloLink.from([sentryErrorLink, authErrorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

const client = new GraphQLClient(process.env.REACT_APP_GRAPHQL_API_URL, {
  credentials: "include",
});

async function withJwtToken(action) {
  let token = getStoredAccessToken();
  if (token && !isValidToken(token)) {
    token = await refreshAccessToken().catch(() => null);
  }

  client.setHeaders({
    Authorization: token ? `JWT ${token}` : "",
    "Content-Type": "application/json",
  });

  try {
    return await action();
  } catch (err) {
    if (!isAuthError(err)) {
      throw err;
    }

    const refreshedToken = await refreshAccessToken();
    client.setHeaders({
      Authorization: `JWT ${refreshedToken}`,
      "Content-Type": "application/json",
    });
    return await action();
  }
}

const sdkClient = getSdk(client, withJwtToken);

export {
  verify,
  sign,
  isValidToken,
  setSession,
  refreshAccessToken,
  revokeRefreshToken,
  reportApolloError,
  userClient,
  sdkClient,
};
